import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ClockIcon, LockClosedIcon, TrophyIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', error:'#EF4444', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };

export default function LevelExam() {
  const [dash, setDash] = useState(null);
  const [phase, setPhase] = useState('intro'); // intro | exam | results
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [flash, setFlash] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30*60); // 30 minutes
  const [result, setResult] = useState(null);
  const [examId, setExamId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/student/dashboard').then(r => setDash(r.data)).catch(()=>{});  // dash.student.cefr_level is the correct path
  }, []);

  useEffect(() => {
    if (phase !== 'exam') return;
    const timer = setInterval(() => setTimeLeft(t => { if (t <= 1) { clearInterval(timer); submitExam(); return 0; } return t-1; }), 1000);
    return () => clearInterval(timer);
  }, [phase]);

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  const startExam = async () => {
    try {
      const res = await api.post('/api/student/level-exam/start');
      const qs = res.data?.questions;
      if (!qs || qs.length === 0) throw new Error('No questions returned');
      setExamId(res.data.exam_id);
      setQuestions(qs);
      setPhase('exam');
    } catch (err) {
      const msg = err?.response?.data?.error || '';
      if (msg) { alert('Cannot start exam: ' + msg); }
      else { setQuestions(getFallback()); setPhase('exam'); }
    }
  };

  const getFallback = () => Array.from({length:20}, (_,i) => ({
    id:i+1, text:`Exam question ${i+1}: Choose the correct answer.`,
    options:['Answer A','Answer B','Answer C','Answer D'], correct:0
  }));

  const handleAnswer = (optIdx) => {
    if (flash) return;
    setSelected(optIdx);
    const q = questions[current];
    const isCorrect = q.answer !== undefined
      ? q.options[optIdx] === q.answer
      : optIdx === q.correct;
    setFlash(isCorrect ? 'correct' : 'wrong');
    setAnswers(prev => ({ ...prev, [q.id]: isCorrect }));
    setTimeout(() => {
      setFlash(null); setSelected(null);
      if (current < questions.length-1) setCurrent(c => c+1);
      else submitExam();
    }, 700);
  };

  const submitExam = async () => {
    const score = Object.values(answers).filter(Boolean).length;
    const total = questions.length || 20;
    const pct = Math.round((score / total) * 100);
    const pass = pct >= 70;
    setResult({ score, total, pass, level:studentLevel || '...', pct });
    setPhase('results');
    if (examId) {
      try {
        const answerList = questions.map(q => ({ question_id: q.id, is_correct: !!answers[q.id] }));
        await api.post('/api/student/level-exam/' + examId + '/submit', { answers: answerList });
      } catch (e) { console.warn('Failed to submit exam to server:', e); }
    }
  };

  const studentLevel = dash?.student?.cefr_level || null;
  const examDone    = dash?.exam_lessons_done ?? 0;
  const examNeeded  = dash?.exam_lessons_required ?? 15;
  const locked = dash === null ? true : dash?.can_take_exam !== true;

  if (phase === 'results' && result) return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <motion.div initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }} className="card" style={{ padding:48, maxWidth:480, textAlign:'center' }}>
        <div style={{ fontSize:64, marginBottom:16 }}>{result.pass ? '🎓' : '📚'}</div>
        <h2 style={{ margin:'0 0 8px', fontSize:26, fontWeight:800, color:result.pass?C.success:C.dark }}>
          {result.pass ? 'Congratulations! You Passed!' : 'Not quite there yet'}
        </h2>
        <div style={{ fontSize:40, fontWeight:900, color:result.pass?C.success:C.warning, margin:'12px 0' }}>
          {result.pct}%
        </div>
        <p style={{ margin:'0 0 20px', color:C.grey }}>{result.score}/{result.total} correct • {result.pass?'70%+ required to pass':'Need 70% to pass'}</p>
        {result.pass ? (
          <div style={{ background:'#D1FAE5', borderRadius:12, padding:20, marginBottom:24 }}>
            <TrophyIcon style={{ width:32, height:32, color:C.success, margin:'0 auto 8px' }} />
            <div style={{ fontWeight:700, color:'#065F46' }}>Certificate earned!</div>
            <div style={{ fontSize:13, color:'#065F46', marginTop:4 }}>Your {result.level} certificate is now available</div>
          </div>
        ) : (
          <div style={{ background:'#FEF9C3', borderRadius:12, padding:20, marginBottom:24 }}>
            <div style={{ fontWeight:700, color:'#92400E', marginBottom:4 }}>Keep practicing!</div>
            <div style={{ fontSize:13, color:'#92400E' }}>Complete more lessons and try again in 7 days</div>
          </div>
        )}
        <div style={{ display:'flex', gap:12 }}>
          <button onClick={() => navigate('/dashboard')} style={{ flex:1, padding:'12px', background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:10, fontWeight:600, cursor:'pointer' }}>
            Dashboard
          </button>
          {result.pass && (
            <button onClick={() => navigate('/my-certificates')} className="btn-teal" style={{ flex:1, padding:'12px' }}>
              View Certificate
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );

  if (phase === 'exam') {
    const q = questions[current];
    return (
      <div style={{ minHeight:'100vh', background:C.bg }}>
        <div style={{ background:C.navy, padding:'12px 32px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ color:'#fff', fontWeight:700 }}>{studentLevel || '...'} Level Exam</div>
          <div style={{ display:'flex', align:'center', gap:8, color: timeLeft<300?C.error:'rgba(255,255,255,0.9)', fontWeight:700, fontSize:16 }}>
            <ClockIcon style={{ width:18, height:18 }} />
            {formatTime(timeLeft)}
          </div>
          <div style={{ color:'rgba(255,255,255,0.7)', fontSize:14 }}>Question {current+1}/{questions.length}</div>
        </div>
        <div style={{ height:4, background:`${C.teal}30` }}>
          <div style={{ height:4, background:C.teal, width:`${(current/questions.length)*100}%`, transition:'width 0.3s' }} />
        </div>
        <div style={{ maxWidth:600, margin:'48px auto', padding:'0 20px' }}>
          <AnimatePresence mode="wait">
            <motion.div key={current} initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-20 }}
              className="card" style={{ padding:40, background:flash==='correct'?'#D1FAE5':flash==='wrong'?'#FEE2E2':'#fff' }}>
              <h2 style={{ margin:'0 0 32px', fontSize:20, fontWeight:700, textAlign:'center' }}>{q?.question || q?.text}</h2>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {q?.options?.map((opt, i) => (
                  <motion.button key={i} whileHover={{ scale:1.01 }} onClick={() => handleAnswer(i)} disabled={!!flash}
                    style={{
                      padding:'14px 20px', borderRadius:10, textAlign:'left', fontSize:15, fontWeight:500, cursor:flash?'default':'pointer',
                      background: selected===i ? (flash==='correct'?'#D1FAE5':'#FEE2E2') : '#F9FAFB',
                      border: `1px solid ${selected===i ? (flash==='correct'?C.success:C.error) : C.border}`,
                      color: selected===i ? (flash==='correct'?'#065F46':'#991B1B') : C.dark,
                    }}>
                    <span style={{ fontWeight:700, marginRight:10 }}>{String.fromCharCode(65+i)}.</span>{opt}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // Intro
  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} className="card" style={{ padding:48, maxWidth:520, textAlign:'center' }}>
        <div style={{ fontSize:56, marginBottom:20 }}>🎓</div>
        <h1 style={{ margin:'0 0 12px', fontSize:26, fontWeight:800 }}>{studentLevel || '...'} Level Exam</h1>
        <p style={{ margin:'0 0 24px', color:C.grey, fontSize:16 }}>Prove your {studentLevel || '...'} proficiency and earn your certificate.</p>
        {locked && (
          <div style={{ background:'#FEF9C3', borderRadius:12, padding:20, marginBottom:24, display:'flex', alignItems:'center', gap:12 }}>
            <LockClosedIcon style={{ width:24, height:24, color:C.warning, flexShrink:0 }} />
            <div style={{ textAlign:'left' }}>
              <div style={{ fontWeight:700, color:'#92400E' }}>Exam locked</div>
              <div style={{ fontSize:13, color:'#92400E', marginTop:2 }}>
                You have completed <strong>{examDone}</strong> of <strong>{examNeeded}</strong> required lessons at {studentLevel || 'your current'} level.
                Complete {Math.max(0, examNeeded - examDone)} more to unlock.
              </div>
            </div>
          </div>
        )}
        <div style={{ background:'#F0F7FA', borderRadius:12, padding:20, marginBottom:28, textAlign:'left' }}>
          {[['⏱ Time limit', '30 minutes'],['❓ Questions', '20 multiple choice'],['✅ Pass mark', '70% (14/20)'],['🏆 Reward', 'Official CEFR certificate']].map(([l,v]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${C.border}`, fontSize:14 }}>
              <span>{l}</span><span style={{ fontWeight:700 }}>{v}</span>
            </div>
          ))}
        </div>
        <button onClick={startExam} disabled={locked} className="btn-teal" style={{ width:'100%', padding:'16px', fontSize:16, opacity:locked?0.5:1, cursor:locked?'not-allowed':'pointer' }}>
          {locked ? '🔒 Exam Locked' : 'Start Exam →'}
        </button>
      </motion.div>
    </div>
  );
}
